import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CardButton from '../../components/Card/CardButton';
import { H3 } from '../../components/Typography';
import { usePagination } from '../../../hooks/usePagination';
import { Button } from '@chakra-ui/react';

const Wrapper = styled.div.attrs({
  className: 'billing-history-container',
})`
  margin-top: 64px;

  h3 {
    margin-bottom: 10px;
  }
`;

const DetailsTable = styled.table.attrs({
  className: 'bundle-details-table',
})`
  width: 100%;

  thead {
    border-bottom: 1px solid #dfe3e8;
    height: 48px;
  }

  tr {
    height: 60px;
  }

  th,
  tr,
  td {
    border: none;
    text-align: center;
  }

  th,
  td {
    font-size: 16px;
    font-weight: 400;
  }

  th {
    color: #637381;
  }

  td {
    color: #000;
  }

  tfoot {
    border-top: 1px solid #dfe3e8;
  }
`;

interface Props {
  billingAttempts: SubscriptionBillingAttempt[];
  originOrder?: OriginOrder;
}

const ITEMS_SHOWN_AT_ONCE = 5;

const BillingHistory: React.FC<Props> = ({ billingAttempts, originOrder }) => {
  const { t } = useTranslation();
  const { endIndex, nextEnabled, setNextPage } = usePagination({
    totalItems: billingAttempts.length + (originOrder ? 1 : 0),
    initialPageSize: ITEMS_SHOWN_AT_ONCE,
  });

  const billingAttemptsToShow = useMemo(() => {
    const bas = [...billingAttempts];

    if (originOrder) {
      const originOrderBillingAttempt = {
        BillingAttemptId: originOrder.name,
        OrderName: originOrder.name,
        CompletedAt: originOrder.createdAt,
        CreatedAt: originOrder.createdAt,
        Ready: true,
      } as SubscriptionBillingAttempt;

      bas.push(originOrderBillingAttempt);
    }

    //sort billing attempts by created at
    const sortedBillingAttempts = bas.sort(
      (
        a: SubscriptionBillingAttempt,
        b: SubscriptionBillingAttempt,
      ): number => {
        const aCreatedAt = a.CompletedAt || a.CreatedAt;
        const bCreatedAt = b.CompletedAt || b.CreatedAt;
        return aCreatedAt > bCreatedAt ? -1 : 1;
      },
    );

    return sortedBillingAttempts.slice(0, endIndex + 1);
  }, [endIndex]);

  const getOrderStatus = (billingAttempt: SubscriptionBillingAttempt) => {
    if (!billingAttempt.Ready) {
      return billingAttempt.NextActionUrl
        ? t('billingHistory.challenged')
        : t('billingHistory.submitted');
    }

    if (billingAttempt.ErrorCode || billingAttempt.ErrorMessage) {
      return t('billingHistory.failed');
    }

    return t('billingHistory.success');
  };

  const getOrderName = (billingAttempt: SubscriptionBillingAttempt) => {
    if (!billingAttempt.Ready && billingAttempt.NextActionUrl) {
      return (
        <Button
          onClick={() => window.open(billingAttempt.NextActionUrl)}
          borderRadius="4px"
        >
          {t('billingHistory.bankChallenge')}
        </Button>
      );
    }
    return !billingAttempt.OrderName ? '-' : billingAttempt.OrderName;
  };

  return billingAttemptsToShow.length ? (
    <Wrapper>
      <H3 modifiers={['heading', 'emphasis']}>{t('billingHistory.title')}</H3>
      <DetailsTable>
        <thead className="bundle-table__header">
          <tr>
            <th style={{ width: '15%' }}>{t('billingHistory.order')}</th>
            <th style={{ width: '45%' }}>{t('billingHistory.date')}</th>
            <th style={{ width: '40%' }}>{t('billingHistory.status')}</th>
          </tr>
        </thead>
        <tbody className="bundle-table__body">
          {billingAttemptsToShow.map((billingAttempt) => (
            <tr
              className="bundle-table__body-row"
              key={billingAttempt.BillingAttemptId}
            >
              <td>{getOrderName(billingAttempt)}</td>
              <td>
                {new Date(
                  billingAttempt.CompletedAt || billingAttempt.CreatedAt,
                ).toLocaleDateString()}
              </td>
              <td>{getOrderStatus(billingAttempt)}</td>
            </tr>
          ))}
        </tbody>
        {nextEnabled && (
          <tfoot className="bundle-table__footer">
            <tr>
              <td colSpan={3}>
                <CardButton onClick={setNextPage}>
                  {t('billingHistory.loadMore')}
                </CardButton>
              </td>
            </tr>
          </tfoot>
        )}
      </DetailsTable>
    </Wrapper>
  ) : null;
};

export default BillingHistory;
